import React from "react";
import { motion } from "framer-motion";

/**
 * Applications — grid of application cards.
 *
 * Two variants:
 *  - variant="dark" (default) — matte-black bg + dark translucent cards with orange radial glow on hover (icon+text side-by-side)
 *  - variant="light" — cream-light bg + white cards with orange icon plate hover (icon+text stacked)
 *
 * Props:
 *  - testId
 *  - eyebrow (default "Applications")
 *  - heading (ReactNode)
 *  - subtitle
 *  - items: { Icon, title, desc }[]
 *  - variant: "dark" | "light"
 *  - gridCols: string (default "lg:grid-cols-3")
 */
export default function Applications({
  testId,
  eyebrow = "Applications",
  heading,
  subtitle,
  items = [],
  variant = "dark",
  gridCols = "lg:grid-cols-3",
}) {
  const isDark = variant === "dark";

  return (
    <section
      data-testid={testId}
      className={`relative py-24 md:py-32 overflow-hidden ${
        isDark
          ? "bg-liftile-matte-black"
          : "bg-liftile-cream-light text-liftile-matte-black"
      }`}
    >
      {isDark && (
        <>
          <div
            aria-hidden
            className="absolute top-0 left-0 right-0 h-px bg-liftile-orange/30"
          />
          <div
            aria-hidden
            className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none opacity-[0.12]"
            style={{
              background:
                "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
              filter: "blur(60px)",
            }}
          />
        </>
      )}

      <div className="container-x relative">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65 }}
          className="max-w-3xl mb-14 md:mb-16"
        >
          <div className="heading-eyebrow mb-3">{eyebrow}</div>
          <h2
            className={`display-2 ${isDark ? "text-liftile-cream" : ""}`}
          >
            {heading}
          </h2>
          {subtitle && (
            <p
              className={`font-body text-base md:text-lg mt-6 leading-relaxed ${
                isDark
                  ? "text-liftile-cream/70"
                  : "text-liftile-matte-black/70"
              }`}
            >
              {subtitle}
            </p>
          )}
        </motion.div>

        <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-6 md:gap-7`}>
          {items.map((a, i) =>
            isDark ? (
              <DarkAppCard key={a.title} item={a} index={i} testId={testId} />
            ) : (
              <LightAppCard key={a.title} item={a} index={i} testId={testId} />
            )
          )}
        </div>
      </div>
    </section>
  );
}

function DarkAppCard({ item, index, testId }) {
  return (
    <motion.div
      data-testid={`${testId}-card-${index}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{
        duration: 0.55,
        delay: index * 0.05,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="group relative h-full rounded-2xl bg-white/[0.03] border border-white/[0.08] p-7 md:p-8 hover:border-liftile-orange/50 hover:bg-white/[0.06] hover:-translate-y-1 transition-all duration-500 overflow-hidden"
    >
      <span
        aria-hidden
        className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.25) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(30px)",
        }}
      />
      <div className="relative flex items-start gap-5">
        <div className="w-12 h-12 rounded-xl bg-liftile-orange/15 flex items-center justify-center shrink-0 group-hover:bg-liftile-orange transition-colors duration-500 p-3">
          <item.Icon
            className="w-6 h-6 text-liftile-orange group-hover:text-white transition-colors duration-500"
            strokeWidth={1.5}
          />
        </div>
        <div>
          <h3 className="font-heading text-base md:text-[17px] uppercase tracking-wider font-semibold text-liftile-cream leading-snug">
            {item.title}
          </h3>
          <p className="font-body text-sm text-liftile-cream/60 mt-2 leading-relaxed">
            {item.desc}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

function LightAppCard({ item, index, testId }) {
  return (
    <motion.div
      data-testid={`${testId}-card-${index}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{
        duration: 0.55,
        delay: index * 0.06,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="group relative h-full flex flex-col bg-white rounded-2xl p-7 md:p-8 shadow-[0_4px_18px_rgba(0,0,0,0.04)] hover:shadow-[0_18px_45px_-12px_rgba(255,107,26,0.22)] border border-transparent hover:border-liftile-orange/40 hover:-translate-y-1 transition-all duration-500"
    >
      <span className="absolute top-0 right-0 h-[2px] w-0 bg-liftile-orange transition-all duration-500 group-hover:w-14 rounded-bl-full" />
      <div className="w-12 h-12 rounded-xl bg-liftile-orange/10 group-hover:bg-liftile-orange flex items-center justify-center transition-colors duration-500">
        <item.Icon
          className="w-5 h-5 text-liftile-orange group-hover:text-white transition-colors duration-500"
          strokeWidth={1.6}
        />
      </div>
      <h3 className="font-heading text-[15px] uppercase tracking-wider font-semibold mt-5 text-liftile-matte-black leading-snug">
        {item.title}
      </h3>
      <p className="font-body text-[14px] text-liftile-matte-black/70 mt-3 leading-relaxed flex-1">
        {item.desc}
      </p>
    </motion.div>
  );
}
